import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/engineering`;

// Stage B forensics: identify the running frontend bundle (CRA main.[hash].js)
// so the backend can record which build issued a digest generation.
export const clientBuildId = () => {
  try {
    const s = Array.from(document.scripts).map((x) => x.src || '').find((src) => /\/main\.[a-f0-9]+\.js/.test(src));
    const m = s && s.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : (process.env.REACT_APP_BUILD_ID || 'dev');
  } catch { return 'unavailable'; }
};
const digestHeaders = (meta = {}) => ({
  'X-Sensbey-Client-Build': clientBuildId(),
  'X-Digest-Ui-Lang': meta.uiLang || 'ja',
  ...(meta.selectedId ? { 'X-Digest-Selected-Id': meta.selectedId } : {}),
});

export const engApi = {
  access: () => axios.get(`${API}/access`).then((r) => r.data),
  members: () => axios.get(`${API}/members`).then((r) => r.data),
  setMembers: (member_user_ids) => axios.put(`${API}/members`, { member_user_ids }).then((r) => r.data),
  projects: () => axios.get(`${API}/projects`).then((r) => r.data),
  inspectionCycles: () => axios.get(`${API}/equipment-inspection-cycles`).then((r) => r.data),
  // カテゴリは自由入力のまま。既存値を候補として配るだけ（マスタは作らない）。
  equipmentCategories: () => axios.get(`${API}/equipment-categories`).then((r) => r.data),
  // 管理部署 / 担当者 の出典。貸出（department_name）と同じ departments、
  // 担当者は employees（人）で users（ログイン）ではない。
  departments: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/departments`).then((r) => r.data),
  employees: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employees`).then((r) => r.data),
  assets: (params) => axios.get(`${API}/assets`, { params }).then((r) => r.data),
  asset: (id) => axios.get(`${API}/assets/${id}`).then((r) => r.data),
  assetPriceHistory: (id) => axios.get(`${API}/assets/${id}/price-history`).then((r) => r.data),
  // 3 · 購入実績の行に請求書を添付（ドキュメントに入り、資産の書類にも出る）
  attachPurchaseInvoice: (rowId, formData) => axios.post(
    `${API}/purchase-records/${rowId}/invoice`, formData).then((r) => r.data),
  createAsset: (b) => axios.post(`${API}/assets`, b).then((r) => r.data),
  updateAsset: (id, b) => axios.put(`${API}/assets/${id}`, b).then((r) => r.data),
  uploadAssetFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/assets/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  deleteAsset: (id) => axios.delete(`${API}/assets/${id}`).then((r) => r.data),
  // 棚マスタ
  shelves: (params) => axios.get(`${API}/shelves`, { params }).then((r) => r.data),
  // B5 · 旧リスト（技術の場所マスタ・IT・備品の自由入力）の取り込み。既定はドライラン。
  locationsSeedPlan: () => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/locations/seed`).then((r) => r.data),
  // 場所マスタは中立の `/api/locations` が正本（部署・コードの決まりはサーバが見る）
  locationDepartments: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/locations/departments`).then((r) => r.data),
  locationCreate: (b) => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/locations`, b).then((r) => r.data),
  locationUpdate: (id, b) => axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${id}`, b).then((r) => r.data),
  locationSetActive: (id, active) => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${id}/active?active=${active}`).then((r) => r.data),
  locationNextCode: (deptCode, purpose) => axios.get(
    `${process.env.REACT_APP_BACKEND_URL}/api/locations/next-code`,
    { params: { dept_code: deptCode || '', purpose: purpose || 'shelf' } }).then((r) => r.data),
  locationsSeedApply: () => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/locations/seed?dry_run=false`).then((r) => r.data),
  // マスタの片付け（場所のコード・部署・重複、単位の寄せ）。既定はドライラン。
  cleanupPlan: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/locations/cleanup`).then((r) => r.data),
  cleanupApply: (kinds) => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/locations/cleanup/apply`,
    null, { params: { confirm: true, ...(kinds && kinds.length ? { kinds: kinds.join(',') } : {}) } }).then((r) => r.data),
  cleanupHistory: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/locations/cleanup/history`).then((r) => r.data),
  cleanupUndo: (logId) => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/locations/cleanup/undo/${logId}`).then((r) => r.data),
  createShelf: (b) => axios.post(`${API}/shelves`, b).then((r) => r.data),
  updateShelf: (id, b) => axios.put(`${API}/shelves/${id}`, b).then((r) => r.data),
  deleteShelf: (id) => axios.delete(`${API}/shelves/${id}`).then((r) => r.data),
  // 場所マスタ（階層: 建物 › 部屋 › 棚 › ビン）
  locations: () => axios.get(`${API}/locations`).then((r) => r.data),
  // 資産カテゴリ（業務用）
  assetCategories: () => axios.get(`${API}/asset-categories`).then((r) => r.data),
  createAssetCategory: (b) => axios.post(`${API}/asset-categories`, b).then((r) => r.data),
  updateAssetCategory: (id, b) => axios.put(`${API}/asset-categories/${id}`, b).then((r) => r.data),
  deleteAssetCategory: (id) => axios.delete(`${API}/asset-categories/${id}`).then((r) => r.data),
  // 品番グループ（採番用）→ 既定カテゴリのマッピング
  skuGroups: () => axios.get(`${API}/sku-groups`).then((r) => r.data),
  // 品目・資産の横断検索は 必要部品モーダルと同じ1本のエンドポイントを使う
  // （mfg_items だけを見る独自クエリを持たない）。
  masterSearch: (q) => axios
    .get(`${process.env.REACT_APP_BACKEND_URL}/api/shizai/parts-request/master-search`,
      { params: { q } })
    .then((r) => r.data),
  unregisteredSkuPrefixes: () => axios.get(`${API}/sku-groups/unregistered`).then((r) => r.data),
  bulkMapSkuGroups: (mappings) => axios.post(`${API}/sku-groups/bulk-map`, { mappings }).then((r) => r.data),
  itemFlowDefinitions: () => axios.get(`${API}/taxonomy/item-flows`).then((r) => r.data),
  stockHealth: () => axios.get(`${API}/taxonomy/stock-health`).then((r) => r.data),
  createSkuGroup: (b) => axios.post(`${API}/sku-groups`, b).then((r) => r.data),
  updateSkuGroup: (code, b) => axios.put(`${API}/sku-groups/${code}`, b).then((r) => r.data),
  deleteSkuGroup: (code) => axios.delete(`${API}/sku-groups/${code}`).then((r) => r.data),
  reclassifyTaxonomy: () => axios.post(`${API}/taxonomy/reclassify`).then((r) => r.data),
  // 他モジュールの読み取り専用マスタ参照
  orderCategoriesList: () => axios.get(`${API}/order-categories`).then((r) => r.data),
  suppliersList: () => axios.get(`${API}/suppliers`).then((r) => r.data),
  useAsset: (id, b) => axios.post(`${API}/assets/${id}/use`, b).then((r) => r.data),
  receiveAsset: (id, b) => axios.post(`${API}/assets/${id}/receive`, b).then((r) => r.data),
  scrapAsset: (id, b) => axios.post(`${API}/assets/${id}/scrap`, b).then((r) => r.data),
  returnAsset: (id, b) => axios.post(`${API}/assets/${id}/return`, b).then((r) => r.data),
  // Phase 2 — 設備台帳 + 貸出
  equipment: (params) => axios.get(`${API}/equipment`, { params }).then((r) => r.data),
  // B2 · 設備番号は共通採番（EQP）。発番せずに次の番号だけ見る。
  equipmentNextNo: () => axios.get(`${API}/equipment/next-no`).then((r) => r.data),
  equipmentDetail: (id) => axios.get(`${API}/equipment/${id}`).then((r) => r.data),
  equipmentDesignParts: (id) => axios.get(`${API}/equipment/${id}/design-parts`).then((r) => r.data),
  setEquipmentOrigin: (id, body) => axios.post(`${API}/equipment/${id}/origin`, body).then((r) => r.data),
  unsetEquipmentOrigin: (id, reason) =>
    axios.delete(`${API}/equipment/${id}/origin`, { params: { reason: reason || undefined } }).then((r) => r.data),
  createEquipment: (b) => axios.post(`${API}/equipment`, b).then((r) => r.data),
  // 設備フォームの任意添付（設備専用の口。技術資産の /assets/upload とは別 origin）
  uploadEquipmentFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/equipment/upload`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  updateEquipment: (id, b) => axios.put(`${API}/equipment/${id}`, b).then((r) => r.data),
  deleteEquipment: (id) => axios.delete(`${API}/equipment/${id}`).then((r) => r.data),
  uploadEquipmentDoc: (id, file, docType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    return axios.post(`${API}/equipment/${id}/docs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  deleteEquipmentDoc: (docId) => axios.delete(`${API}/equipment/docs/${docId}`).then((r) => r.data),
  equipmentDocUrl: (docId) => `${API}/equipment/docs/${docId}`,
  requestRental: (equipmentId, b) => axios.post(`${API}/equipment/${equipmentId}/rent`, b).then((r) => r.data),
  rentals: (params) => axios.get(`${API}/rentals`, { params }).then((r) => r.data),
  approveRental: (id) => axios.post(`${API}/rentals/${id}/approve`).then((r) => r.data),
  rejectRental: (id, reason) => axios.post(`${API}/rentals/${id}/reject`, { reason }).then((r) => r.data),
  returnRental: (id, note) => axios.post(`${API}/rentals/${id}/return`, { note }).then((r) => r.data),
  cancelRental: (id) => axios.post(`${API}/rentals/${id}/cancel`).then((r) => r.data),
  // Phase 3 — データ収集
  forms: () => axios.get(`${API}/forms`).then((r) => r.data),
  form: (id) => axios.get(`${API}/forms/${id}`).then((r) => r.data),
  createForm: (b) => axios.post(`${API}/forms`, b).then((r) => r.data),
  updateForm: (id, b) => axios.put(`${API}/forms/${id}`, b).then((r) => r.data),
  deleteForm: (id) => axios.delete(`${API}/forms/${id}`).then((r) => r.data),
  uploadFormFile: (formId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/forms/${formId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  formFileUrl: (fileId) => `${API}/forms/files/${fileId}`,
  submitFormResponse: (formId, answers) => axios.post(`${API}/forms/${formId}/responses`, { answers }).then((r) => r.data),
  formResponses: (formId) => axios.get(`${API}/forms/${formId}/responses`).then((r) => r.data),
  deleteFormResponse: (rid) => axios.delete(`${API}/forms/responses/${rid}`).then((r) => r.data),
  formExportUrl: (formId) => `${API}/forms/${formId}/export.csv`,
  formExportZipUrl: (formId) => `${API}/forms/${formId}/export.zip`,
  checkFormAttachments: (formId) => axios.get(`${API}/forms/${formId}/attachments/check`).then((r) => r.data),
  requestFormReupload: (formId, responseId, body) =>
    axios.post(`${API}/forms/${formId}/responses/${responseId}/request-reupload`, body).then((r) => r.data),
  bulkRequestFormReupload: (formId, body) =>
    axios.post(`${API}/forms/${formId}/responses/bulk-request-reupload`, body).then((r) => r.data),
  // extras — 保全記録 / リマインダー
  addMaintenance: (equipmentId, b) => axios.post(`${API}/equipment/${equipmentId}/maintenance`, b).then((r) => r.data),
  deleteMaintenance: (mid) => axios.delete(`${API}/equipment/maintenance/${mid}`).then((r) => r.data),
  runOverdueReminders: () => axios.post(`${API}/rentals/reminders/run`).then((r) => r.data),
  // Phase 4 — 日報 (Daily Reports)
  reports: (params) => axios.get(`${API}/reports`, { params }).then((r) => r.data),
  report: (id) => axios.get(`${API}/reports/${id}`).then((r) => r.data),
  createReport: (b) => axios.post(`${API}/reports`, b).then((r) => r.data),
  updateReport: (id, b) => axios.patch(`${API}/reports/${id}`, b).then((r) => r.data),
  submitReport: (id) => axios.post(`${API}/reports/${id}/submit`).then((r) => r.data),
  deleteReport: (id) => axios.delete(`${API}/reports/${id}`).then((r) => r.data),
  reportsMeta: () => axios.get(`${API}/reports/meta`).then((r) => r.data),
  myReportDraft: () => axios.get(`${API}/reports/my-draft`).then((r) => r.data),
  myReportStats: () => axios.get(`${API}/reports/my-stats`).then((r) => r.data),
  teamStreaks: () => axios.get(`${API}/reports/team-streaks`).then((r) => r.data),
  uploadReportFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/reports/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  reportFileUrl: (fileId) => `${API}/reports/files/${fileId}`,
  reportAiSuggestField: (b) => axios.post(`${API}/reports/ai-suggest-field`, b).then((r) => r.data),
  reportAiPrompts: (reportId) => axios.get(`${API}/reports/ai-prompts`, { params: reportId ? { report_id: reportId } : {} }).then((r) => r.data),
  reportAiPromptDelete: (pid) => axios.delete(`${API}/reports/ai-prompts/${pid}`).then((r) => r.data),
  reportTranslate: (rid, force = false) => axios.post(`${API}/reports/${rid}/translate`, null, { params: force ? { force: true } : {} }).then((r) => r.data),
  reportAiAssist: async (b, onStage) => {
    // NDJSON stream — keepalive lines prevent Cloudflare 520 on slow LLM.
    // Backend emits `{"heartbeat":true}` every ~8s while the model runs,
    // then a single final line — `{"fields":{...}}` on success or
    // `{"error":"..."}` on failure. Any HTTP error is thrown as before.
    const res = await fetch(`${API}/reports/ai-assist`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(b),
    });
    if (!res.ok) {
      let detail = 'AI生成に失敗しました';
      try { const j = await res.json(); detail = j.detail || detail; } catch (_e) { /* keep default */ }
      const err = new Error(detail);
      err.response = { data: { detail } };
      throw err;
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '', last = null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n')) !== -1) {
        const line = buf.slice(0, idx).trim();
        buf = buf.slice(idx + 1);
        if (!line) continue;
        try {
          const obj = JSON.parse(line);
          if (obj.heartbeat) continue;
          if (obj.stage) { onStage?.(obj); continue; }
          last = obj;
        } catch (_e) { /* ignore malformed line */ }
      }
    }
    if (buf.trim()) {
      try { last = JSON.parse(buf.trim()); } catch (_e) { /* keep previous */ }
    }
    if (!last || last.error) {
      const detail = (last && last.error) || 'AI生成に失敗しました';
      const err = new Error(detail);
      err.response = { data: { detail } };
      throw err;
    }
    return last;
  },
  reportsDashboard: (params) => axios.get(`${API}/reports/dashboard`, { params }).then((r) => r.data),
  reportDigests: () => axios.get(`${API}/reports/digests`).then((r) => r.data),
  reportDigest: (id) => axios.get(`${API}/reports/digests/${id}`, { headers: { 'X-Sensbey-Client-Build': clientBuildId() } }).then((r) => r.data),
  reportDigestsTrend: (weeks = 8) => axios.get(`${API}/reports/digests-trend`, { params: { weeks } }).then((r) => r.data),
  generateReportDigest: (b, meta) => axios.post(`${API}/reports/digests/generate`, b, { headers: digestHeaders(meta) }).then((r) => r.data),
  regenerateReportDigest: (id, meta) => axios.post(`${API}/reports/digests/${id}/regenerate`, null, { headers: digestHeaders({ selectedId: id, ...meta }) }).then((r) => r.data),
  cancelReportDigest: (id) => axios.post(`${API}/reports/digests/${id}/cancel`).then((r) => r.data),
  listRunningDigests: () => axios.get(`${API}/reports/digests/running`).then((r) => r.data),
  translateReportDigest: (id, opts = {}) => axios.post(`${API}/reports/digests/${id}/translate`,
    null, { params: opts.force ? { force: 'true' } : {} }).then((r) => r.data),
  cancelReportDigestTranslation: (id) => axios.post(`${API}/reports/digests/${id}/translate/cancel`).then((r) => r.data),
  reportDigestTranslationStatus: (id) => axios.get(`${API}/reports/digests/${id}/translations/en/status`).then((r) => r.data),
  reportDigestTranslationHistory: (id) => axios.get(`${API}/reports/digests/${id}/translations/history`).then((r) => r.data),
  restoreReportDigestTranslation: (id, tid) => axios.post(`${API}/reports/digests/${id}/translations/${tid}/restore`).then((r) => r.data),
  deleteReportDigest: (id) => axios.delete(`${API}/reports/digests/${id}`).then((r) => r.data),
  // Stage F — 不変バージョン履歴 / immutable version history
  digestVersions: (sid) => axios.get(`${API}/reports/digests/${sid}/versions`).then((r) => r.data),
  digestVersion: (sid, vid) => axios.get(`${API}/reports/digests/${sid}/versions/${vid}`).then((r) => r.data),
  compareDigestVersions: (sid, a, b) => axios.get(`${API}/reports/digests/${sid}/versions/compare`, { params: { a, b } }).then((r) => r.data),
  activateDigestVersion: (sid, vid, body) => axios.post(`${API}/reports/digests/${sid}/versions/${vid}/activate`, body).then((r) => r.data),
  restoreDigestVersion: (sid, vid, body = {}) => axios.post(`${API}/reports/digests/${sid}/versions/${vid}/restore`, body).then((r) => r.data),
  migrateDigestVersions: (dry_run = true) => axios.post(`${API}/reports/digests/versions/migrate`, { dry_run }).then((r) => r.data),
  unarchiveReportDigest: (sid) => axios.post(`${API}/reports/digests/${sid}/unarchive`).then((r) => r.data),
  reportSettings: () => axios.get(`${API}/reports/settings`).then((r) => r.data),
  saveReportSettings: (b) => axios.put(`${API}/reports/settings`, b).then((r) => r.data),
  // カスタムタイトル（Personal Knowledge 用の共有タイトルマスタ）
  customTitles: () => axios.get(`${API}/reports/custom-titles`).then((r) => r.data),
  createCustomTitle: (title) => axios.post(`${API}/reports/custom-titles`, { title }).then((r) => r.data),
  // Phase 5 — 週次 プロジェクト/タイトル → ナレッジ 出力
  kbExportSettings: () => axios.get(`${API}/kb-export/settings`).then((r) => r.data),
  saveKbExportSettings: (b) => axios.put(`${API}/kb-export/settings`, b).then((r) => r.data),
  kbExportPreview: (b) => axios.post(`${API}/kb-export/preview`, b || {}).then((r) => r.data),
  kbExportRun: (b) => axios.post(`${API}/kb-export/run`, { ...(b || {}), confirm: true }).then((r) => r.data),
  kbExportHistory: (params) => axios.get(`${API}/kb-export/history`, { params }).then((r) => r.data),
  kbExportHistoryDetail: (id) => axios.get(`${API}/kb-export/history/${id}`).then((r) => r.data),
};

export const ASSET_STATUS_LABEL = { in_stock: '在庫あり', out_of_stock: '在庫なし', reserved: '予約済', in_use: '使用中', scrapped: 'スクラップ' };
export const ASSET_STATUS_BADGE = {
  in_stock: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  out_of_stock: 'bg-rose-50 text-rose-800 border-rose-300',
  reserved: 'bg-amber-50 text-amber-900 border-amber-300',
  in_use: 'bg-sky-50 text-sky-800 border-sky-300',
  scrapped: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

// 資産カテゴリのバッジ配色（部品=青 / 消耗品=橙 / 設備=紫 / 工具=藍 / 成果品=緑 / 文書=灰）
export const CATEGORY_BADGE = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  gray: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};
export const CATEGORY_DOT = {
  blue: 'bg-blue-500', orange: 'bg-orange-500', purple: 'bg-purple-500',
  indigo: 'bg-indigo-500', green: 'bg-emerald-500', gray: 'bg-zinc-400',
};
export const FLOW_TYPE_LABEL = {
  component: '部品フロー（予約→組込→返却/消費）',
  consumable: '消耗品フロー（入庫→消費・返却なし）',
  equipment: '設備・工具フロー（設置→貸出→返却→保全）',
  output: '成果品フロー（評価→展開→製造/スクラップ）',
  none: '在庫操作なし（未分類・移行データ専用）',
};
// B5 · 一つの場所マスタ（製造の 倉庫 / ゾーン も同じ語彙に乗る）
export const LOCATION_TYPE_LABEL = {
  building: '建物', room: '部屋', warehouse: '倉庫', zone: 'ゾーン', shelf: '棚', bin: 'ビン',
};

export const EQUIP_STATUS_LABEL = { available: '利用可', rented: '貸出中', maintenance: 'メンテナンス', retired: '廃棄' };
export const EQUIP_STATUS_BADGE = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rented: 'bg-amber-50 text-amber-700 border-amber-200',
  maintenance: 'bg-sky-50 text-sky-700 border-sky-200',
  retired: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};
export const DOC_TYPE_LABEL = { program: 'プログラム', wiring: '配線図', drawing: '技術図面', manual: 'マニュアル', other: 'その他' };
export const RENTAL_STATUS_LABEL = { pending: '承認待ち', approved: '貸出中', rejected: '却下', returned: '返却済み', cancelled: '取消' };
export const RENTAL_STATUS_BADGE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-sky-50 text-sky-700 border-sky-200',
  rejected: 'bg-rose-50 text-rose-600 border-rose-200',
  returned: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

export const FORM_STATUS_LABEL = { draft: '下書き', open: '受付中', closed: '受付終了' };
export const FORM_STATUS_BADGE = {
  draft: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  open: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-amber-50 text-amber-700 border-amber-200',
};
export const FIELD_TYPE_LABEL = {
  text: '短文テキスト', textarea: '長文テキスト', number: '数値', select: 'プルダウン',
  radio: '単一選択', checkbox: '複数選択', date: '日付', time: '時刻',
  photo: '写真（カメラ）', file: 'ファイル添付',
};
export const MAINT_TYPE_LABEL = {
  repair: '修理', inspection: '点検', parts_replace: '部品交換', calibration: '校正', other: 'その他',
};
export const MAINT_TYPE_BADGE = {
  repair: 'bg-rose-50 text-rose-600 border-rose-200',
  inspection: 'bg-sky-50 text-sky-700 border-sky-200',
  parts_replace: 'bg-amber-50 text-amber-700 border-amber-200',
  calibration: 'bg-violet-50 text-violet-700 border-violet-200',
  other: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

export const REPORT_STATUS_LABEL = {
  planned: '計画', in_progress: '進行中', blocked: 'ブロック中', waiting: '待機中', completed: '完了',
};
export const REPORT_STATUS_BADGE = {
  planned: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  in_progress: 'bg-sky-50 text-sky-700 border-sky-200',
  blocked: 'bg-rose-50 text-rose-600 border-rose-200',
  waiting: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
export const REPORT_DOC_LABEL = { draft: '下書き', submitted: '提出済み' };
export const REPORT_DOC_BADGE = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  submitted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

// 在庫ステータスは常に実数量から導出する（保存値 status ではなく数量が根拠）。
// サーバーが stock_status を返すのでそれを優先し、古い応答にはフォールバックする。
export const stockStatusOf = (a) => {
  if (!a) return 'out_of_stock';
  if (a.stock_status) return a.stock_status;
  const free = Number(a.qty_free ?? a.qty_available ?? 0) || 0;
  const used = Number(a.qty_used ?? 0) || 0;
  const total = Number(a.qty_total ?? 0) || 0;
  const scrapped = Number(a.qty_scrapped ?? 0) || 0;
  const reserved = Number(a.qty_reserved ?? 0) || 0;
  if (free > 0) return 'in_stock';
  if (reserved > 0) return 'reserved';
  if (used > 0) return 'in_use';
  if (scrapped > 0 && scrapped >= total) return 'scrapped';
  return 'out_of_stock';
};
